/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataResult, process, State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridDataStateChangeEvent,
  GridDetailRowProps,
  GridExpandChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
  AggregateClientData,
  AggregateClientDataFilterKey,
  AggregateClientDataWithTotal,
} from 'api/Wallet/Types/AggregateClientData'
import { FlexBox } from 'components/Box'
import { Link } from 'components/Link'
import { Loading } from 'components/Loading'
import { Option, Select } from 'components/Select'
import { generateClassNameForReverseType } from 'helpers/transactions'
import { useAppDispatch, useAppSelector } from 'store'
import { CompositeFilterDescriptor, FilterDescriptor } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchTransactionStatusAction } from 'store/transactions/transaction.action'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { dateComparator, formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { instanceOfFilterDescriptor } from 'utils/QueryHelpers'
import { TransactionStatus } from 'utils/transaction/status.index'

import { getTransactionsFundingSummaryDetailsColumns } from './TransactionsFundingSummaryOverview/transactionsFundingSummaryDetailsColumns'

const useMapStateToProps = () => ({
  transactionStatus: useAppSelector((state) => state.transactions.status),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const walletsApiClient = useApiClient(WalletsApiClient)
  return {
    fetchTransactionStatus: async () => dispatch(fetchTransactionStatusAction(walletsApiClient)),
  }
}

const createDataState = (
  state: State,
  clientPaymentProviderTransaction: AggregateClientData[],
  collapse = true
) => {
  const groups = state.group
  const aggregates: { field: string; aggregate: 'count' | 'sum' | 'average' | 'min' | 'max' }[] = [
    {
      field: 'amount',
      aggregate: 'sum',
    },
    {
      field: 'currency',
      aggregate: 'min',
    },
  ]
  if (groups) {
    groups.map((group) => (group.aggregates = aggregates))
  }
  let result = process(clientPaymentProviderTransaction, state)
  if (collapse) {
    result = { ...result, data: result.data.map((dataItem) => ({ ...dataItem, expanded: false })) }
  }
  return {
    result,
    dataState: state,
  }
}

export const AggregateDataTable = ({
  clientId,
  showBankName = false,
}: {
  clientId?: string
  showBankName?: boolean
}): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const { callback: fetchClientAggregateTransactions, forceRefresh } =
    useCallbackWithForceRefresh(async () => {
      if (!clientId) {
        return []
      }
      return walletsApiClient.getClientAggregatedDataQuery(clientId, getQuery())
    }, [clientId])
  const { data = [], isLoading } = useFetchOne(fetchClientAggregateTransactions)
  const [aggregatedTotals, setAggregatedTotals] = useState<AggregateClientDataWithTotal>()
  const [dataState, setDataState] = useState<{ result: DataResult; dataState: State }>(
    createDataState(getPaginationInitialState(), data)
  )
  const [filterDescriptor, setFilterDescriptor] = useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [
      {
        field: AggregateClientDataFilterKey.transactionState,
        operator: '==',
        value: TransactionStatus.Completed,
      },
    ],
  })

  const { transactionStatus } = useMapStateToProps()
  const { fetchTransactionStatus } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      if (clientId && data) {
        if (isEmpty(transactionStatus)) await fetchTransactionStatus()
        setDataState(createDataState(dataState.dataState, data))
      }
    })()
  }, [data])

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(createDataState(event.dataState, data))
  }

  const selectTransactionState = useCallback((): FilterDescriptor | undefined => {
    const transactionState = filterDescriptor.filters.find(
      (x: FilterDescriptor | CompositeFilterDescriptor) => {
        if (instanceOfFilterDescriptor(x)) {
          return x.field === AggregateClientDataFilterKey.transactionState
        }
        return false
      }
    )

    if (transactionState && instanceOfFilterDescriptor(transactionState)) {
      return transactionState
    }
  }, [filterDescriptor.filters])

  const selectFilterId = () => {
    const transactionStateId = selectTransactionState()?.value
    if (transactionStateId) {
      return transactionStatus.find((x) => x.id === transactionStateId)?.id
    }
  }

  const handleTransactionStatusChange = async (statusId: number) => {
    if (clientId) {
      if (data && data.length >= 0) {
        setDataState(createDataState(dataState.dataState, data))
        setFilterDescriptor({
          ...filterDescriptor,
          filters: [
            {
              field: AggregateClientDataFilterKey.transactionState,
              operator: '==',
              value: statusId,
            },
          ],
        })
      }

      forceRefresh()
      return data
    }
  }

  const handleExpand = (event: GridExpandChangeEvent) => {
    setDataState({
      ...dataState,
      result: {
        ...dataState.result,
        data: dataState.result.data.map((x) =>
          x.lastDate === event.dataItem.lastDate ? { ...x, expanded: event.value } : x
        ),
      },
    })
  }

  const fetchTotals = async () => {
    if (clientId) {
      const aggregatedTotalsFetch = await walletsApiClient.getClientAggregatedDataTotalQuery(
        clientId,
        {
          ToCurrencyId: 'USD',
        }
      )
      setAggregatedTotals(aggregatedTotalsFetch)
    }
  }

  useEffect(() => {
    fetchTotals()
  }, [clientId])

  const getQuery = useCallback(() => {
    return new URLSearchParams({
      [AggregateClientDataFilterKey.transactionState]: selectTransactionState()?.value,
    }).toString()
  }, [selectTransactionState])

  const pageChange = async (event: GridPageChangeEvent) => {
    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())
    setDataState(createDataState({ take: event.page.take, skip: event.page.skip }, data))
  }

  return (
    <Loading isLoading={isLoading}>
      <FlexBox alignItems='center' justifyContent='flex-end'>
        <FlexBox mb={15}>
          <Select
            placeholder='Select filter'
            defaultValue={selectFilterId()}
            onChange={handleTransactionStatusChange}
            style={{ width: '180px' }}
            data-test='transaction-status-filter-field'
          >
            {transactionStatus.map((x) => (
              <Option value={x.id} key={x.id}>
                {x.name}
              </Option>
            ))}
          </Select>
        </FlexBox>
      </FlexBox>
      <Grid
        resizable
        pageable={pageableSettings}
        data={dataState.result}
        detail={(gridDetailProps) => (
          <DetailComponent
            dataIndex={gridDetailProps.dataIndex}
            dataItem={gridDetailProps.dataItem}
            showBankName={showBankName}
          />
        )}
        {...dataState.dataState}
        onDataStateChange={handleDataStateChange}
        expandField='expanded'
        onPageChange={pageChange}
        onExpandChange={handleExpand}
      >
        <Column
          title='Wallet'
          cell={(gridCellProps: GridCellProps) => (
            <td
              className='table-columns__content'
              style={{ color: 'var(--link)', textDecoration: 'underline' }}
            >
              <Link
                to={{
                  pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                    id: gridCellProps.dataItem.wallet.id,
                    slug: 'details',
                  }),
                }}
              >
                {gridCellProps.dataItem.wallet.name}
              </Link>
            </td>
          )}
          className='table-columns__content'
        />
        <Column
          title='Payment Provider'
          field='paymentProvider.name'
          cell={(gridCellProps: GridCellProps) => {
            const paymentProviderName = gridCellProps.dataItem?.paymentProvider.name
            const paymentProviderExternalName = gridCellProps.dataItem?.paymentProvider.externalName

            if (paymentProviderExternalName) {
              return (
                <td className={generateClassNameForReverseType(gridCellProps.dataItem)}>
                  <Tooltip key='top'>
                    <TooltipTrigger render={<span />}>{paymentProviderName}</TooltipTrigger>
                    <TooltipContent side='top'>{paymentProviderExternalName}</TooltipContent>
                  </Tooltip>
                </td>
              )
            }
            return (
              <td className={generateClassNameForReverseType(gridCellProps.dataItem)}>
                {paymentProviderName}
              </td>
            )
          }}
        />
        <Column
          title='Transaction Type'
          field='transactionType.name'
          className='table-columns__content'
        />
        <Column
          title='Amount'
          field='amount'
          className='table-columns__number'
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__number'>{gridCellProps.dataItem.amount.toFixed(2)}</td>
          )}
          footerCell={() => (
            <td className='table-columns__number'>
              Wallet Total: {aggregatedTotals?.convertedAmountTotal}
            </td>
          )}
        />
        <Column
          title='Currency'
          field='currency.id'
          className='table-columns__content'
          footerCell={() => <td>{aggregatedTotals?.convertedCurrency.id}</td>}
        />
        <Column title='Count' field='transactionCount' className='table-columns__number' />
        <Column
          title='Last Date'
          cell={(gridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.lastDate, true)}</td>
          )}
        />
      </Grid>
    </Loading>
  )
}

interface DetailComponentProps extends GridDetailRowProps {
  showBankName?: boolean
}

const DetailComponent: React.FC<DetailComponentProps> = (props) => {
  const { showBankName = false } = props
  const aggregateClientData: AggregateClientData = props.dataItem

  const hasAggregateClientData = !!aggregateClientData

  const columns = getTransactionsFundingSummaryDetailsColumns(showBankName)

  if (hasAggregateClientData) {
    return (
      <Grid
        data={aggregateClientData.externalAccounts
          .sort((a, b) => dateComparator(a.lastDate, b.lastDate, true))
          .map((x) => ({ ...aggregateClientData, externalAccount: x }))}
        resizable
      >
        {columns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
      </Grid>
    )
  }

  return (
    <div style={{ height: '50px', width: '100%' }}>
      <div style={{ position: 'absolute', width: '100%' }}>
        <div className='k-loading-image' />
      </div>
    </div>
  )
}
